import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const categoryImages: Record<number, string> = {
  0: 'https://readdy.ai/api/search-image?query=Minimalist%20sneaker%20product%20shot%20on%20pure%20white%20clean%20studio%20lighting%20front%20angle%20view%20soft%20shadow%20isolated%20fashion%20footwear%20editorial&width=80&height=80&seq=nav-cat-shoes&orientation=squarish&nocache=true',
  1: 'https://readdy.ai/api/search-image?query=Minimalist%20grey%20hoodie%20sweater%20folded%20neatly%20on%20white%20surface%20top%20down%20flatlay%20clean%20studio%20lighting%20soft%20shadow%20cozy%20aesthetic&width=80&height=80&seq=nav-cat-hoodies&orientation=squarish&nocache=true',
  2: 'https://readdy.ai/api/search-image?query=Clean%20white%20cotton%20tshirt%20folded%20on%20white%20surface%20minimal%20studio%20lighting%20top%20view%20simple%20elegant%20basic%20fashion&width=80&height=80&seq=nav-cat-tshirts&orientation=squarish&nocache=true',
  3: 'https://readdy.ai/api/search-image?query=Minimalist%20beige%20jacket%20laid%20flat%20on%20white%20surface%20clean%20studio%20lighting%20top%20down%20view%20soft%20fabric%20texture%20elegant%20outerwear&width=80&height=80&seq=nav-cat-jackets&orientation=squarish&nocache=true',
  4: 'https://readdy.ai/api/search-image?query=Minimalist%20tailored%20trousers%20folded%20on%20white%20surface%20clean%20studio%20lighting%20flat%20lay%20view%20soft%20fabric%20neutral%20color&width=80&height=80&seq=nav-cat-pants&orientation=squarish&nocache=true',
  5: 'https://readdy.ai/api/search-image?query=Clean%20baseball%20cap%20on%20white%20surface%20top%20view%20minimal%20studio%20lighting%20simple%20elegant%20headwear%20isolated&width=80&height=80&seq=nav-cat-headwear&orientation=squarish&nocache=true',
  6: 'https://readdy.ai/api/search-image?query=Matching%20outfit%20set%20folded%20on%20white%20surface%20clean%20studio%20lighting%20top%20view%20coordinated%20fashion%20pieces&width=80&height=80&seq=nav-cat-sets&orientation=squarish&nocache=true',
  7: 'https://readdy.ai/api/search-image?query=Clean%20cotton%20underwear%20basics%20on%20white%20surface%20minimal%20studio%20lighting%20flat%20lay%20simple%20essentials&width=80&height=80&seq=nav-cat-underwear&orientation=squarish&nocache=true',
  8: 'https://readdy.ai/api/search-image?query=Sports%20jersey%20folded%20on%20white%20surface%20clean%20studio%20lighting%20top%20view%20athletic%20wear%20minimal&width=80&height=80&seq=nav-cat-jersey&orientation=squarish&nocache=true',
  9: 'https://readdy.ai/api/search-image?query=Minimalist%20watch%20and%20bracelet%20on%20white%20surface%20clean%20studio%20lighting%20top%20view%20elegant%20accessories%20isolated&width=80&height=80&seq=nav-cat-accessories&orientation=squarish&nocache=true',
  10: 'https://readdy.ai/api/search-image?query=Minimalist%20leather%20handbag%20on%20white%20surface%20clean%20studio%20lighting%20front%20view%20elegant%20bag%20isolated&width=80&height=80&seq=nav-cat-bags&orientation=squarish&nocache=true',
  11: 'https://readdy.ai/api/search-image?query=Leather%20belt%20coiled%20on%20white%20surface%20clean%20studio%20lighting%20top%20view%20minimal%20elegant%20accessory%20isolated&width=80&height=80&seq=nav-cat-belts&orientation=squarish&nocache=true',
  12: 'https://readdy.ai/api/search-image?query=Elegant%20perfume%20bottle%20on%20white%20surface%20clean%20studio%20lighting%20front%20view%20luxury%20fragrance%20isolated%20minimal&width=80&height=80&seq=nav-cat-perfumes&orientation=squarish&nocache=true',
  13: 'https://readdy.ai/api/search-image?query=Various%20fashion%20items%20scattered%20on%20white%20surface%20clean%20studio%20lighting%20top%20view%20diverse%20products&width=80&height=80&seq=nav-cat-other&orientation=squarish&nocache=true',
};

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          {/* Logo + Home */}
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight whitespace-nowrap"
            >
              OOPBUY
            </Link>
            <Link
              to="/"
              className="hidden md:inline-flex text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              {t('nav.home')}
            </Link>
          </div>

          {/* Desktop: Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            {/* Categories Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <button className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg transition-colors whitespace-nowrap cursor-pointer">
                <span>{t('nav.categories')}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              <div
                className={`absolute top-full left-0 mt-1 w-[620px] bg-background-50/98 backdrop-blur-xl rounded-xl border border-background-200/80 overflow-hidden transition-all duration-200 ${
                  dropdownOpen ? 'opacity-100 translate-y-0 visible' : 'opacity-0 -translate-y-1 invisible'
                }`}
              >
                <div className="p-4 grid grid-cols-2 gap-1">
                  {categories.map((cat, idx) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                        <img
                          src={categoryImages[idx]}
                          alt={cat.name}
                          className="w-full h-full object-cover object-top"
                          loading="lazy"
                        />
                      </div>
                      <span className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Shopping Guide Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setGuideOpen(true)}
              onMouseLeave={() => setGuideOpen(false)}
            >
              <button className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg transition-colors whitespace-nowrap cursor-pointer">
                <span>{t('nav.shopping_guide')}</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}></i>
              </button>

              <div
                className={`absolute top-full left-0 mt-1 w-[320px] bg-background-50/98 backdrop-blur-xl rounded-xl border border-background-200/80 overflow-hidden transition-all duration-200 ${
                  guideOpen ? 'opacity-100 translate-y-0 visible' : 'opacity-0 -translate-y-1 invisible'
                }`}
              >
                <div className="p-3">
                  <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-3 py-2">
                    {t('nav.shopping_guide')}
                  </p>
                  {articles.map((a) => (
                    <Link
                      key={a.slug}
                      to={`/artikel/${a.slug}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors group"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 group-hover:bg-primary-100 transition-colors">
                        <i className="ri-article-line text-sm text-foreground-600 group-hover:text-primary-600"></i>
                      </span>
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 block truncate">{a.title}</span>
                        <span className="text-xs text-foreground-400">{a.readingTime}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop: CTA Buttons */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-medium text-foreground-700 border border-foreground-300/60 rounded-full hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              oopbuy spreadsheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1.5"
            >
              {t('button.buy_now')}
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>
          </div>

          {/* Mobile: Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Menü öffnen"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-0 top-16 bg-background-50 z-40 transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Home Link */}
          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-3 px-5 py-4 border-b border-background-200 hover:bg-background-100 transition-colors"
          >
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-50">
              <i className="ri-home-4-line text-base text-primary-500"></i>
            </span>
            <span className="text-sm font-semibold text-foreground-900">{t('nav.home')}</span>
          </Link>

          {/* Categories with images */}
          <div className="px-4 py-5">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
              {t('nav.categories')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat, idx) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-background-100 transition-colors text-center"
                >
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-background-100 flex-shrink-0">
                    <img
                      src={categoryImages[idx]}
                      alt={cat.name}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                  <span className="text-xs font-medium text-foreground-700 leading-tight">{cat.name}</span>
                </a>
              ))}
            </div>
          </div>

          <hr className="mx-4 border-background-200" />

          {/* Shopping Guide */}
          <div className="px-4 py-5">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-1">
              {t('nav.shopping_guide')}
            </p>
            <div className="space-y-1">
              {articles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/artikel/${a.slug}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors"
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 flex-shrink-0">
                    <i className="ri-article-line text-sm text-foreground-600"></i>
                  </span>
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-foreground-800 block truncate">{a.title}</span>
                    <span className="text-xs text-foreground-400">{a.readingTime}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Mobile CTA */}
          <div className="mt-auto flex flex-col gap-3 px-4 py-6 border-t border-background-200">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="w-full py-3 text-sm font-medium text-center text-foreground-700 border border-foreground-300/60 rounded-full hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              oopbuy spreadsheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              onClick={() => setMobileOpen(false)}
              className="w-full py-3 text-sm font-semibold text-center text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('button.buy_now')}
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}